/**
 * Рецептура замеса.
 *
 * Рецептура действует с какого-то дня и до тех пор, пока её не сменит новая:
 * сегодня работаем по одной, завтра по другой, а отчёты за прошлые дни должны
 * и через год считаться по той рецептуре, которая действовала тогда. Поэтому
 * старая не правится и не удаляется — у неё проставляется день окончания.
 *
 * Расход задаётся вилкой «от и до» на замес: технолог держит допуск, а не
 * единственное число, и выход за вилку — это повод посмотреть, а не ошибка.
 */
import { z } from 'zod';
const dateField = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Дата в формате ГГГГ-ММ-ДД');
export const recipeItemSchema = z
    .object({
    materialTypeId: z.string().uuid(),
    qtyPerBatchMinG: z.number().int().min(0).max(10_000_000),
    qtyPerBatchMaxG: z.number().int().min(0).max(10_000_000),
    /**
     * Взаимозаменяемое сырьё: строки с одной пометкой проверяются по сумме.
     * Капсулят идёт в замещение или в дополнение агломерата.
     */
    substituteGroup: z.string().trim().min(1).max(40).nullish(),
})
    .refine((item) => item.qtyPerBatchMaxG >= item.qtyPerBatchMinG, {
    message: 'Верхняя граница расхода меньше нижней',
    path: ['qtyPerBatchMaxG'],
});
export const recipeCreateSchema = z
    .object({
    name: z.string().trim().min(2, 'Назовите рецептуру').max(120),
    /** Номинальный вес замеса. Всё остальное считается на него. */
    batchWeightG: z.number().int().min(1_000).max(10_000_000),
    /** С какого дня работаем по этой рецептуре. */
    validFrom: dateField,
    items: z.array(recipeItemSchema).min(1, 'Добавьте хотя бы одно сырьё'),
})
    .superRefine((recipe, ctx) => {
    // У взаимозаменяемого сырья вилка расхода должна быть одна на группу:
    // проверяется их сумма, и две разные вилки на одну сумму означали бы,
    // что правило зависит от того, каким сырьём его выполнили.
    const groups = new Map();
    for (const item of recipe.items) {
        const group = item.substituteGroup;
        if (!group)
            continue;
        const seen = groups.get(group);
        if (!seen) {
            groups.set(group, { min: item.qtyPerBatchMinG, max: item.qtyPerBatchMaxG });
            continue;
        }
        if (seen.min !== item.qtyPerBatchMinG || seen.max !== item.qtyPerBatchMaxG) {
            ctx.addIssue({
                code: 'custom',
                path: ['items'],
                message: `У взаимозаменяемого сырья «${group}» вилка расхода должна совпадать`,
            });
            return;
        }
    }
});
/**
 * Сравнение фактического расхода с рецептурой.
 *
 * Взаимозаменяемое сырьё складывается: 70 кг агломерата, 70 кг капсулята
 * и 35 на 35 — одинаково правильно, а 70 плюс 70 уже перерасход. Порознь
 * такое правило не выразить, поэтому группа проверяется как одна строка.
 *
 * Расход считается на замес: за смену их бывает разное количество, и без
 * деления сравнивать не с чем.
 */
export function checkRecipeUsage(rules, usedByMaterialG, batches) {
    if (batches <= 0)
        return [];
    const singles = rules.filter((rule) => !rule.substituteGroup);
    const grouped = new Map();
    for (const rule of rules) {
        if (!rule.substituteGroup)
            continue;
        const list = grouped.get(rule.substituteGroup) ?? [];
        list.push(rule);
        grouped.set(rule.substituteGroup, list);
    }
    const checks = [];
    for (const rule of singles) {
        checks.push({
            label: rule.name,
            usedG: usedByMaterialG[rule.materialTypeId] ?? 0,
            minG: rule.qtyPerBatchMinG,
            maxG: rule.qtyPerBatchMaxG,
        });
    }
    for (const list of grouped.values()) {
        const first = list[0];
        checks.push({
            label: list.map((rule) => rule.name).join(' или '),
            usedG: list.reduce((sum, rule) => sum + (usedByMaterialG[rule.materialTypeId] ?? 0), 0),
            minG: first.qtyPerBatchMinG,
            maxG: first.qtyPerBatchMaxG,
        });
    }
    const deviations = [];
    for (const check of checks) {
        // Округляем к ближайшему грамму: дробных граммов в учёте нет
        const perBatchG = Math.round(check.usedG / batches);
        if (perBatchG < check.minG) {
            deviations.push({ ...check, perBatchG, kind: 'below' });
        }
        else if (perBatchG > check.maxG) {
            deviations.push({ ...check, perBatchG, kind: 'above' });
        }
    }
    return deviations;
}
//# sourceMappingURL=recipes.js.map